import { Component, DestroyRef, ElementRef, inject, Inject, OnInit, ViewChild } from '@angular/core';
import { MatAutocompleteSelectedEvent, MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogTitle, MatDialogContent, MatDialogActions, MatDialogClose } from '@angular/material/dialog';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { UntypedFormBuilder, UntypedFormGroup, FormControl, Validators, ReactiveFormsModule } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { map, startWith, finalize, switchMap } from 'rxjs/operators';
import { Document, DocumentType, Mortgage, Building, Customer, Application, DocumentClassificationResult } from '@app/core/stingray-api/models';
import { DocumentsService } from '@app/core/stingray-api/services';
import moment from 'moment';
import { GoogleAnalyticsService } from '@app/core/analytics/google-analytics.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatChipsModule, MatChipGrid, MatChipRow, MatChipRemove, MatChipInput } from "@angular/material/chips";
import { MatButton } from '@angular/material/button';
import { LoadingButtonComponent } from '../../loading-button/loading-button.component';
import { digitsOnlyDirective } from '../../directives/digits-only.directive';
import { MatDatepickerInput, MatDatepickerToggle, MatDatepicker } from '@angular/material/datepicker';
import { MatIcon } from '@angular/material/icon';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { AsyncPipe, PercentPipe } from '@angular/common';
import { MatInput } from '@angular/material/input';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { TranslateModule } from '@ngx-translate/core';

export interface FileDialogData {
  document: Document;
  documentTypes: DocumentType[];
  customers: Customer[];
  applications: Application[];
  buildings: Building[];
  mortgages: Mortgage[];
  objectType: 'Building' | 'Mortgage' | 'Application' | 'Customer';
  /**
   * The automatic classification of a just uploaded document, so the user can check and correct it
   */
  classification?: DocumentClassificationResult;
}

@Component({
    selector: 'unite-file-dialog',
    templateUrl: './file-dialog.component.html',
    styleUrls: ['./file-dialog.component.scss'],
    imports: [MatDialogTitle, TranslateModule, ReactiveFormsModule, MatDialogContent, MatFormField, MatLabel, MatInput, MatAutocompleteTrigger, MatAutocomplete, MatOption, MatSelect, MatChipGrid, MatChipRow, MatChipRemove, MatIcon, MatChipInput, MatDatepickerInput, MatDatepickerToggle, MatSuffix, MatDatepicker, digitsOnlyDirective, MatDialogActions, LoadingButtonComponent, MatButton, MatDialogClose, AsyncPipe, PercentPipe]
})
export class FileDialogComponent implements OnInit {

  private destroyRef = inject(DestroyRef);
  // Chip options
  selectable = true;
  removable = true;
  separatorKeysCodes: number[] = [ENTER, COMMA];

  document: Document;
  documentFormGroup: UntypedFormGroup;
  classification?: DocumentClassificationResult;
  isSuggestion = false;

  associationType: 'Building' | 'Mortgage' | 'Customer' | 'Person' | 'Company' | 'Collaboration';
  documentTypes: DocumentType[] = [];
  documentTypesFiltered: Observable<DocumentType[]>;

  customers: Customer[] = [];
  showCustomerInput = false;

  loadingSave = false;

  @ViewChild('applicationInput', { static: true }) applicationInput: ElementRef<HTMLInputElement>;
  @ViewChild('applicationAutocomplete', { static: true }) applicationAutocomplete: MatAutocomplete;
  applicationFormControl = new FormControl();
  applications: Application[] = [];
  applicationsFiltered: Observable<Application[]>;
  applicationsAll: Application[] = [];

  @ViewChild('buildingInput', { static: true }) buildingInput: ElementRef<HTMLInputElement>;
  @ViewChild('buildingAutoComplete', { static: true }) buildingAutocomplete: MatAutocomplete;
  buildingFormControl = new FormControl();
  buildings: Building[] = [];
  buildingsFiltered: Observable<Building[]>;
  buildingsAll: Building[] = [];

  @ViewChild('mortgageInput', { static: true }) mortgageInput: ElementRef<HTMLInputElement>;
  @ViewChild('mortageAutocomplete', { static: true }) mortageAutocomplete: MatAutocomplete;
  mortgageFormControl = new FormControl();
  mortgages: Mortgage[] = [];
  mortgagesFiltered: Observable<Mortgage[]>;
  mortgagesAll: Mortgage[] = [];

  constructor(private documentsService: DocumentsService,
    private formBuilder: UntypedFormBuilder,
    private matDialogRef: MatDialogRef<FileDialogComponent>,
    private googleAnalyticsService: GoogleAnalyticsService,
    @Inject(MAT_DIALOG_DATA) private fileEditData: FileDialogData) {
    this.documentFormGroup = this.formBuilder.group({
      documentType: [],
      description: [, [Validators.required]],
      issued: [],
      expires: [],
      year: [],

      customer: [],
      buildings: [],
      mortgages: [],
      applications: []
    });

    this.document = this.fileEditData.document;
    this.classification = this.fileEditData.classification;
    // The detected type was not stored (not confident enough); links found by lender reference may still be stored
    this.isSuggestion = !!this.classification && !this.document.documentType;
    this.documentTypes = this.fileEditData.documentTypes;
    this.customers = this.fileEditData.customers;
    this.applicationsAll = this.fileEditData.applications;
    this.buildingsAll = this.fileEditData.buildings;
    this.mortgagesAll = this.fileEditData.mortgages;
  }

  ngOnInit() {
    this.initDocumentType();
    this.initApplication();
    this.initBuilding();
    this.initMortgage();

    this.documentFormGroup.patchValue(this.document, { emitEvent: false });
    this.documentFormGroup.get('customer').setValue(
      this.customers.find(customer => customer.id === this.document.customer.id), { emitEvent: false }
    );

    if (this.document.issued) {
      this.documentFormGroup.get('issued').setValue(moment(this.document.issued));
    }
    if (this.document.expires) {
      this.documentFormGroup.get('expires').setValue(moment(this.document.expires));
    }

    this.applications = this.document.applications;
    this.buildings = this.document.buildings;
    this.mortgages = this.document.mortgages;
    if (this.fileEditData.objectType) {
      this.documentTypes = this.documentTypes.filter((documentType: DocumentType) => {
        if (this.fileEditData.objectType && this.fileEditData.objectType === 'Application') {
          return true;
        }
        if (this.fileEditData.objectType && this.fileEditData.objectType === 'Building' && documentType.associationType === 'Building') {
          return true;
        }
        if (this.fileEditData.objectType && this.fileEditData.objectType === 'Mortgage' && documentType.associationType === 'Mortgage') {
          return true;
        }
        if (this.fileEditData.objectType && this.fileEditData.objectType === 'Customer' && (
          documentType.associationType === 'Person' ||
          documentType.associationType === 'Company' ||
          documentType.associationType === 'Collaboration' ||
          documentType.associationType === 'Customer')) {
          return true;
        }
        return false;
      });

    }

    if (this.document.documentType) {
      this.associationType = this.document.documentType.associationType;
      this.documentFormGroup.get('documentType').setValue(
        this.documentTypes.find(documentType => documentType.id === this.document.documentType.id), { emitEvent: false }
      );
      this.showCustomerInput = this.checkCustomerInput();
    } else {
      this.associationType = null;
      this.showCustomerInput = false;
    }

    this.prefillSuggestion();
  }

  /**
   * Prefills the form with a detected classification that was not confident enough to be stored,
   * so the user only has to confirm or correct it. Nothing is stored until the user saves.
   */
  private prefillSuggestion() {
    const suggestion = this.classification?.classification;
    if (!this.isSuggestion || !suggestion?.documentTypeId) {
      return;
    }

    const documentType = this.documentTypes.find(type => type.id === suggestion.documentTypeId);
    if (!documentType) {
      return;
    }

    this.associationType = documentType.associationType;
    this.documentFormGroup.get('documentType').setValue(documentType, { emitEvent: false });
    this.showCustomerInput = this.checkCustomerInput();

    if (suggestion.issued) {
      this.documentFormGroup.get('issued').setValue(moment(suggestion.issued));
    }
    if (suggestion.expires) {
      this.documentFormGroup.get('expires').setValue(moment(suggestion.expires));
    }
    if (suggestion.year) {
      this.documentFormGroup.get('year').setValue(suggestion.year);
    }

    // Suggested links: the person the document belongs to, or the buildings/mortgages it is about
    const customer = this.customers.find(c => c.id === suggestion.customerId);
    if (customer) {
      this.documentFormGroup.get('customer').setValue(customer, { emitEvent: false });
    }
    if (documentType.associationType === 'Building' && !this.buildings?.length && suggestion.buildingIds?.length) {
      this.buildings = this.buildingsAll.filter(building => suggestion.buildingIds.includes(building.id));
    }
    if (documentType.associationType === 'Mortgage' && !this.mortgages?.length && suggestion.mortgageIds?.length) {
      this.mortgages = this.mortgagesAll.filter(mortgage => suggestion.mortgageIds.includes(mortgage.id));
    }
  }

  /**
   * Save the document
   */
  save() {
    if (this.documentFormGroup.valid && this.associationType != null) {
      this.loadingSave = true;

      const document = <Document>{
        id: this.document.id,
        documentType: this.documentFormGroup.get('documentType').value,
        description: this.documentFormGroup.get('description').value
      };

      if (document.documentType) {
        document.issued = document.documentType.hasIssueDate && this.documentFormGroup.get('issued').value ?
          (<moment.Moment>this.documentFormGroup.get('issued').value).locale('en').format('L') : null;
        document.expires = document.documentType.hasExpirationDate && this.documentFormGroup.get('expires').value ?
          (<moment.Moment>this.documentFormGroup.get('expires').value).locale('en').format('L') : null;
        document.year = document.documentType.hasYear ? this.documentFormGroup.get('year').value : null;
      }

      document.applications = this.applications;

      document.customer = this.documentFormGroup.get('customer').value || this.customers[0];
      document.buildings = this.associationType === 'Building' ? this.buildings : [];
      document.mortgages = this.associationType === 'Mortgage' ? this.mortgages : [];

      this.googleAnalyticsService.trackEvent('document', 'edit');

      this.documentsService.patchDocument({
        id: document.id, body: [
          { path: 'documentType', value: document.documentType, op: 'replace' },
          { path: 'description', value: document.description, op: 'replace' },
          { path: 'issued', value: document.issued, op: 'replace' },
          { path: 'expires', value: document.expires, op: 'replace' },
          { path: 'year', value: document.year, op: 'replace' },
          { path: 'customer/id', value: document.customer.id, op: 'replace' },
          { path: 'applications', value: document.applications, op: 'replace' },
          { path: 'buildings', value: document.buildings, op: 'replace' },
          { path: 'mortgages', value: document.mortgages, op: 'replace' }
        ]
      }).pipe(
        // Saving the document type in this dialog is the manual validation of the document
        switchMap((patchedDocument: Document) => patchedDocument.documentType
          ? this.documentsService.validateDocument({ id: patchedDocument.id })
          : of(patchedDocument)),
        finalize(() => {
          this.loadingSave = false;
        })
      ).subscribe((patchDocument: Document) => {
        this.matDialogRef.close(patchDocument);
      });
    }
  }

  /**
   * Remove the document
   */
  remove() {
    if (this.document.id) {

      this.googleAnalyticsService.trackEvent('document', 'remove');
      this.documentsService.deleteDocument({ id: this.document.id }).subscribe(() => {
        this.matDialogRef.close(null);
      });
    } else {
      this.matDialogRef.close(null);
    }
  }

  displayDocumentType(documentType: DocumentType): string {
    return documentType ? documentType.id + '. ' + documentType.name : '';
  }

  removeApplication(application: Application): void {
    this.applications.splice(this.applications.indexOf(application), 1);
  }

  selectedApplication(event: MatAutocompleteSelectedEvent): void {
    this.applications.push(event.option.value);
    this.applicationInput.nativeElement.value = '';
    this.applicationFormControl.setValue('', { emitEvent: true });
  }

  removeBuilding(building: Building): void {
    this.buildings.splice(this.buildings.indexOf(building), 1);
  }

  selectedBuilding(event: MatAutocompleteSelectedEvent): void {
    this.buildings.push(event.option.value);
    this.buildingInput.nativeElement.value = '';
    this.buildingFormControl.setValue('', { emitEvent: true });
  }

  removeMortgage(mortgage: Mortgage): void {
    this.mortgages.splice(this.mortgages.indexOf(mortgage), 1);
  }

  selectedMortgage(event: MatAutocompleteSelectedEvent): void {
    this.mortgages.push(event.option.value);
    this.mortgageInput.nativeElement.value = '';
    this.mortgageFormControl.setValue('', { emitEvent: true });
  }

  private checkCustomerInput(): boolean {
    if (this.associationType === 'Building' || this.associationType === 'Mortgage') {
      return false;
    }
    if (this.customers[0].type === 'Person') {
      return false;
    }
    if (this.customers[0].type === 'Company' && this.associationType === 'Company') {
      return false;
    }
    if (this.customers[0].type === 'Collaboration' && (this.associationType === 'Company' || this.associationType === 'Collaboration')) {
      return false;
    }
    return true;
  }

  private initDocumentType(): void {
    this.documentTypesFiltered = this.documentFormGroup.get('documentType').valueChanges.pipe(
      map((value: string | DocumentType) => {
        if (typeof value === 'string') {
          this.associationType = null;
          if (value !== '') {
            this.showCustomerInput = false;
          }
          this.documentFormGroup.get('customer').setValue(null, { emitEvent: false });
          return this.filterDocumentType(value);
        } else {
          this.associationType = value.associationType;
          this.showCustomerInput = this.checkCustomerInput();
          this.documentFormGroup.get('description').setValue(this.documentFormGroup.get('description').value + ' - ' + value.name);

          return this.filterDocumentType(value.name);
        }
      }, takeUntilDestroyed(this.destroyRef))
    );

    setTimeout(() => {
      if (this.documentTypes.length === 1) {
        this.documentFormGroup.get('documentType').setValue(this.documentTypes[0]);
      }
    }, 500);
  }

  private filterDocumentType(value: string): DocumentType[] {
    const filterValue = value.toLowerCase();
    return this.documentTypes.filter((documentType: DocumentType) => {
      return this.displayDocumentType(documentType).toLocaleLowerCase().includes(filterValue) ||
        documentType.name.toLowerCase().includes(filterValue) ||
        documentType.id.includes(value);
    });
  }

  private initApplication(): void {


    this.applicationsFiltered = this.applicationFormControl.valueChanges.pipe(
      startWith(''),
      map((value: string | Application) => {
        if (typeof value === 'string') {
          return this.filterApplication(value);
        } else {
          return this.filterApplication(value.name);
        }
      })
    );


  }

  private filterApplication(value: string): Application[] {
    const filterValue = value.toLowerCase();

    return this.applicationsAll.filter((application: Application) =>
      !this.applications.find(selectedApplication => selectedApplication.id === application.id)
      && (
        application.name.toLowerCase().includes(filterValue) ||
        application.mortgage.lender.name.toLowerCase().includes(filterValue)));
  }

  private initBuilding(): void {
    this.buildingsFiltered = this.buildingFormControl.valueChanges.pipe(
      startWith(''),
      takeUntilDestroyed(this.destroyRef),
      map((value: string | Building) => {
        if (typeof value === 'string') {
          return this.filterBuilding(value);
        } else {
          return this.filterBuilding(value.address.addressLine1);
        }
      })
    );


  }

  private filterBuilding(value: string): Building[] {
    const filterValue = value.toLowerCase();

    return this.buildingsAll.filter((building: Building) =>
      !this.buildings.find(selectedBuilding => selectedBuilding.id === building.id)
      && (
        building.address.city.toLowerCase().includes(filterValue) ||
        building.address.postalCode.toLowerCase().includes(filterValue) ||
        building.address.addressLine1.toLowerCase().includes(filterValue)));
  }


  private initMortgage(): void {
    this.mortgagesFiltered = this.mortgageFormControl.valueChanges.pipe(
      startWith(''),
      takeUntilDestroyed(this.destroyRef),
      map((value: string | Mortgage) => {
        if (typeof value === 'string') {
          return this.filterMortgage(value);
        } else {
          return this.filterMortgage(value.number);
        }
      })
    );
  }

  private filterMortgage(value: string): Mortgage[] {
    const filterValue = value.toLowerCase();

    return this.mortgagesAll.filter((mortgage: Mortgage) =>
      !this.mortgages.find(selectedMortage => selectedMortage.id === mortgage.id)
      && (
        mortgage.number.toLowerCase().includes(filterValue) ||
        mortgage.lender.name.toLowerCase().includes(filterValue)));
  }
}
